import { LANGUAGE_CODE } from '@getyoti/react-face-capture';
import { Interval } from '@components/result/age-antispoofing/age-displayer/range-age-displayer/RangeAgeDisplayer';
import {
    AgeDisplayMode,
    AgeEstimationModelField,
    AntispoofingModelField,
    Endpoint,
    ImageValidationLevelField,
    LevelOfAssuranceField,
    MetadataField,
    MultiframeMode,
    OperatorField,
    SecureMode,
} from '@models/prediction';
import { getArrayFromString } from '@utils/types';

const viteEnv = import.meta.env;

/**
 * Check the different arguments to provide a valid configuration value for
 * numbers.
 *
 * @param envValue The value from the environment variable.
 * @param deploymentValue The deployment value from the script "start.sh".
 * @param defaultValue The default value in case the previous arguments are not
 * valid.
 * @returns The configuration value to be used as default in the deployment.
 */
function getNumberConfigValue(
    envValue: number | undefined,
    deploymentValue: number,
    defaultValue: number | undefined,
) {
    if (envValue !== undefined && !isNaN(envValue)) return envValue;
    if (deploymentValue != undefined && !isNaN(deploymentValue)) return deploymentValue;
    return defaultValue;
}

/**
 * Check the different arguments to provide a valid configuration value for
 * strings.
 *
 * @param envValue The value from the environment variable.
 * @param deploymentValue The deployment value from the script "start.sh".
 * @param defaultValue The default value in case the previous arguments are not
 * valid.
 * @returns The configuration value to be used as default in the deployment.
 */
function getStringConfigValue(
    envValue: string | undefined,
    deploymentValue: string,
    defaultValue: string | undefined,
) {
    if (envValue) return envValue;
    if (deploymentValue) return deploymentValue;
    return defaultValue;
}

export default {
    // In development environment use the REACT_BACKEND_URL env variable. In
    // production, use the ${BACKEND_URL} placeholder. It will be substituted
    // before running the application (See 'docker/start.sh' script).
    // Remember that env variables must have VITE prefix.
    apiUrl: viteEnv.VITE_BACKEND || 'https://${BACKEND_URL}/api',
    version: viteEnv.VITE_VERSION || '',
    env: viteEnv.MODE,
    stdDevThreshold:
        parseFloat(
            viteEnv.VITE_STD_DEV_THRESHOLD
                ? viteEnv.VITE_STD_DEV_THRESHOLD
                : '${STD_DEV_THRESHOLD}',
        ) || 6.0, // Default stdDevThreshold provided by R&D team to determine when a predicted age is accurate.
    showFaceMatching:
        (viteEnv.VITE_SHOW_FACE_MATCHING
            ? viteEnv.VITE_SHOW_FACE_MATCHING
            : '${SHOW_FACE_MATCHING}') === 'true',
    // TODO: This is temporary. When the user role system will be done, we will delete this option.
    showStoreImagesOption:
        (viteEnv.VITE_SHOW_STORE_IMAGES_OPTION
            ? viteEnv.VITE_SHOW_STORE_IMAGES_OPTION
            : '${SHOW_STORE_IMAGES_OPTION}') === 'true',
    levelAssuranceValues: getArrayFromString(
        viteEnv.VITE_LEVEL_ASSURANCE_VALUES
            ? viteEnv.VITE_LEVEL_ASSURANCE_VALUES
            : '${LEVEL_ASSURANCE_VALUES}',
    ) as LevelOfAssuranceField[],
    consentCheckboxLabel: viteEnv.VITE_CONSENT_CHECKBOX_LABEL || '${CONSENT_CHECKBOX_LABEL}',
    consentText1: viteEnv.VITE_CONSENT_TEXT_1 || '${CONSENT_TEXT_1}',
    consentText2: viteEnv.VITE_CONSENT_TEXT_2 || '${CONSENT_TEXT_2}',
    showConsent:
        (viteEnv.VITE_SHOW_CONSENT ? viteEnv.VITE_SHOW_CONSENT : '${SHOW_CONSENT}') === 'true',
    checkboxConsentConfigMessage:
        viteEnv.VITE_CHECKBOX_CONSENT_CONFIG_MESSAGE || '${CHECKBOX_CONSENT_CONFIG_MESSAGE}',
    allowedRolesForEndpointsConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_ENDPOINTS_CONFIG || '${ALLOWED_ROLES_FOR_ENDPOINTS_CONFIG}',
    ),
    allowedRolesForMetadataConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_METADATA_CONFIG || '${ALLOWED_ROLES_FOR_METADATA_CONFIG}',
    ),
    allowedRolesForLevelOfAssuranceConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_LEVEL_OF_ASSURANCE_CONFIG ||
            '${ALLOWED_ROLES_FOR_LEVEL_OF_ASSURANCE_CONFIG}',
    ),
    allowedRolesForAgeEstimationModelConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_AGE_ESTIMATION_MODEL_CONFIG ||
            '${ALLOWED_ROLES_FOR_AGE_ESTIMATION_MODEL_CONFIG}',
    ),
    allowedRolesForAntispoofingModelConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_ANTISPOOFING_MODEL_CONFIG ||
            '${ALLOWED_ROLES_FOR_ANTISPOOFING_MODEL_CONFIG}',
    ),
    allowedRolesForOperatorConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_OPERATOR_CONFIG || '${ALLOWED_ROLES_FOR_OPERATOR_CONFIG}',
    ),
    allowedRolesForThresholdConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_THRESHOLD_CONFIG || '${ALLOWED_ROLES_FOR_THRESHOLD_CONFIG}',
    ),
    allowedRolesForSecureModeConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_SECURE_MODE_CONFIG ||
            '${ALLOWED_ROLES_FOR_SECURE_MODE_CONFIG}',
    ),
    allowedRolesForMultiframeModeConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_MULTIFRAME_MODE_CONFIG ||
            '${ALLOWED_ROLES_FOR_MULTIFRAME_MODE_CONFIG}',
    ),
    allowedRolesForConfigWrapperConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_CONFIG_WRAPPER_CONFIG ||
            '${ALLOWED_ROLES_FOR_CONFIG_WRAPPER_CONFIG}',
    ),
    allowedRolesForAgeDisplayModeConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_AGE_DISPLAY_MODE_CONFIG ||
            '${ALLOWED_ROLES_FOR_AGE_DISPLAY_MODE_CONFIG}',
    ),
    allowedRolesForLanguageConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_LANGUAGE_CONFIG || '${ALLOWED_ROLES_FOR_LANGUAGE_CONFIG}',
    ),
    allowedEndpointsForAdmin: getArrayFromString(
        viteEnv.VITE_ALLOWED_ENDPOINTS_FOR_ADMIN || '${ALLOWED_ENDPOINTS_FOR_ADMIN}',
    ),
    allowedEndpointsForTester: getArrayFromString(
        viteEnv.VITE_ALLOWED_ENDPOINTS_FOR_TESTER || '${ALLOWED_ENDPOINTS_FOR_TESTER}',
    ),
    allowedEndpointsForUser: getArrayFromString(
        viteEnv.VITE_ALLOWED_ENDPOINTS_FOR_USER || '${ALLOWED_ENDPOINTS_FOR_USER}',
    ),
    allowedEndpointsForDataCollector: getArrayFromString(
        viteEnv.VITE_ALLOWED_ENDPOINTS_FOR_DATA_COLLECTOR ||
            '${ALLOWED_ENDPOINTS_FOR_DATA_COLLECTOR}',
    ),
    allowedRolesForImageValidationLevelConfig: getArrayFromString(
        viteEnv.VITE_ALLOWED_ROLES_FOR_IMAGE_VALIDATION_LEVEL_CONFIG ||
            '${ALLOWED_ROLES_FOR_IMAGE_VALIDATION_LEVEL_CONFIG}',
    ),
    defaultConfig: {
        endpoint: (viteEnv.VITE_DEFAULT_ENDPOINTS_CONFIG ||
            '${DEFAULT_ENDPOINTS_CONFIG}') as Endpoint,
        levelOfAssurance: getStringConfigValue(
            viteEnv.VITE_DEFAULT_LEVEL_OF_ASSURANCE_CONFIG,
            '${DEFAULT_LEVEL_OF_ASSURANCE_CONFIG}',
            undefined,
        ) as LevelOfAssuranceField,
        ageEstimationModel: getStringConfigValue(
            viteEnv.VITE_DEFAULT_AGE_ESTIMATION_MODEL_CONFIG,
            '${DEFAULT_AGE_ESTIMATION_MODEL_CONFIG}',
            undefined,
        ) as AgeEstimationModelField,
        antispoofingModel: getStringConfigValue(
            viteEnv.VITE_DEFAULT_ANTISPOOFING_MODEL_CONFIG,
            '${DEFAULT_ANTISPOOFING_MODEL_CONFIG}',
            undefined,
        ) as AntispoofingModelField,
        metadata: getStringConfigValue(
            viteEnv.VITE_DEFAULT_METADATA_CONFIG,
            '${DEFAULT_METADATA_CONFIG}',
            undefined,
        ) as MetadataField,
        operator: getStringConfigValue(
            viteEnv.VITE_DEFAULT_OPERATOR_CONFIG,
            '${DEFAULT_OPERATOR_CONFIG}',
            undefined,
        ) as OperatorField,
        threshold: getNumberConfigValue(
            parseInt(viteEnv.VITE_DEFAULT_THRESHOLD_CONFIG || '', 10),
            parseInt('${DEFAULT_THRESHOLD_CONFIG}', 10),
            undefined,
        ),
        imageValidationLevel: (viteEnv.VITE_DEFAULT_IMAGE_VALIDATION_LEVEL_CONFIG ||
            '${DEFAULT_IMAGE_VALIDATION_LEVEL_CONFIG}') as ImageValidationLevelField,
        mode: (viteEnv.VITE_DEFAULT_MODE_CONFIG || '${DEFAULT_MODE_CONFIG}') as SecureMode,
        multiframeMode: (viteEnv.VITE_DEFAULT_MULTIFRAME_MODE_CONFIG ||
            '${DEFAULT_MULTIFRAME_MODE_CONFIG}') as MultiframeMode,
        ageDisplayMode: (viteEnv.VITE_DEFAULT_AGE_DISPLAY_MODE_CONFIG ||
            '${DEFAULT_AGE_DISPLAY_MODE_CONFIG}') as AgeDisplayMode,
        ageDisplayInterval: Number(
            viteEnv.VITE_AGE_DISPLAY_INTERVAL_CONFIG || '${AGE_DISPLAY_INTERVAL_CONFIG}',
        ) as Interval,
        language: (viteEnv.VITE_DEFAULT_LANGUAGE_CONFIG ||
            '${DEFAULT_LANGUAGE_CONFIG}') as LANGUAGE_CODE,
    },
    slowRequestNotificationTime:
        parseFloat(
            viteEnv.VITE_SLOW_REQUEST_NOTIFICATION_TIME
                ? viteEnv.VITE_SLOW_REQUEST_NOTIFICATION_TIME
                : '${SLOW_REQUEST_NOTIFICATION_TIME}',
        ) || 5000, // Default slowRequestNotificationTime to 5000.
};
